import { Edit, FileText, Plus, Save, Trash2, X } from 'lucide-react';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import api from '../../services/api';

const SiteContentManagement = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  const [contentType, setContentType] = useState('footer');
  const queryClient = useQueryClient();

  // 사이트 콘텐츠 목록 조회
  const { data: siteContentsResponse, isLoading } = useQuery(
    'site-contents',
    () => api.get('/super-admin/site-contents'),
    {
      onError: (error) => {
        toast.error('사이트 콘텐츠 목록을 불러오는데 실패했습니다.');
      }
    }
  );

  const siteContentsList = siteContentsResponse?.data || [];

  // 콘텐츠 타입별 이름 매핑
  const contentTypeNames = {
    footer: 'Footer',
    terms_of_service: '이용약관',
    privacy_policy: '개인정보처리방침',
    privacy_consent: '개인정보제공동의'
  };

  // 콘텐츠 생성/수정 Mutation
  const saveContentMutation = useMutation(
    (data) => {
      if (selectedContent) {
        return api.put(`/super-admin/site-contents/${selectedContent.id}`, data);
      } else {
        return api.post('/super-admin/site-contents', data);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('site-contents');
        setShowModal(false);
        setSelectedContent(null);
        toast.success(selectedContent ? '콘텐츠가 수정되었습니다.' : '콘텐츠가 생성되었습니다.');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '콘텐츠 저장에 실패했습니다.');
      }
    }
  );

  // 콘텐츠 삭제 Mutation
  const deleteContentMutation = useMutation(
    (id) => api.delete(`/super-admin/site-contents/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('site-contents');
        toast.success('콘텐츠가 삭제되었습니다.');
      },
      onError: (error) => {
        toast.error('콘텐츠 삭제에 실패했습니다.');
      }
    }
  );

  const handleEdit = (content) => {
    setSelectedContent(content);
    setContentType(content.content_type);
    setShowModal(true);
  };

  const handleCreate = (type) => {
    setSelectedContent(null);
    setContentType(type);
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('정말 삭제하시겠습니까?')) {
      deleteContentMutation.mutate(id);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = {
      content_type: contentType,
      title: formData.get('title'),
      content: formData.get('content'),
      company_name: formData.get('company_name') || null,
      company_address: formData.get('company_address') || null,
      company_phone: formData.get('company_phone') || null,
      company_email: formData.get('company_email') || null,
      business_number: formData.get('business_number') || null,
      copyright_text: formData.get('copyright_text') || null,
    };

    saveContentMutation.mutate(data);
  };

  // 각 타입별 콘텐츠 가져오기
  const getContentByType = (type) => {
    return siteContentsList.find(c => c.content_type === type);
  };

  return (
    <div className="space-y-6">
      {/* 콘텐츠 타입별 카드 */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Object.entries(contentTypeNames).map(([type, name]) => {
          const content = getContentByType(type);
          return (
            <div key={type} className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-4">
                <div className="flex items-center mb-3">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <FileText className="h-5 w-5 text-blue-600" />
                    </div>
                  </div>
                  <div className="ml-3 flex-1 min-w-0">
                    <dl>
                      <dt className="text-xs font-medium text-gray-500 truncate">{name}</dt>
                      <dd className="text-base font-medium text-gray-900 mt-1">
                        {content ? `버전 ${content.version}` : '미등록'}
                      </dd>
                    </dl>
                  </div>
                </div>
                <div className="flex items-center justify-center w-full">
                  {content ? (
                    <div className="flex items-center space-x-2 w-full">
                      <button
                        onClick={() => handleEdit(content)}
                        className="flex-1 inline-flex items-center justify-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <Edit className="mr-1 h-3 w-3" />
                        수정
                      </button>
                      <button
                        onClick={() => handleDelete(content.id)}
                        className="flex-1 inline-flex items-center justify-center px-3 py-1.5 border border-red-300 shadow-sm text-xs font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        <Trash2 className="mr-1 h-3 w-3" />
                        삭제
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleCreate(type)}
                      className="w-full inline-flex items-center justify-center px-3 py-2 border border-transparent text-xs font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <Plus className="mr-1 h-3 w-3" />
                      생성
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* 콘텐츠 목록 */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {isLoading ? (
          <ul className="divide-y divide-gray-200">
            <li className="px-4 py-4">
              <div className="animate-pulse flex space-x-4">
                <div className="rounded-full bg-gray-200 h-10 w-10"></div>
                <div className="flex-1 space-y-2 py-1">
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                </div>
              </div>
            </li>
          </ul>
        ) : siteContentsList.length === 0 ? (
          <ul className="divide-y divide-gray-200">
            <li className="px-4 py-8 text-center">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">콘텐츠가 없습니다</h3>
              <p className="mt-1 text-sm text-gray-500">
                첫 번째 콘텐츠를 생성해보세요.
              </p>
            </li>
          </ul>
        ) : (
          <ul className="divide-y divide-gray-200">
            {siteContentsList.map((content) => (
              <li key={content.id}>
                <div className="px-4 py-4 flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <FileText className="h-6 w-6 text-purple-600" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="flex items-center">
                        <p className="text-sm font-medium text-gray-900">
                          {contentTypeNames[content.content_type] || content.content_type}
                        </p>
                        {content.version && (
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            버전 {content.version}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{content.title}</p>
                      {content.updated_at && (
                        <p className="text-xs text-gray-400">
                          최종 수정: {new Date(content.updated_at).toLocaleDateString('ko-KR')}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEdit(content)}
                      className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <Edit className="mr-1 h-3 w-3" />
                      수정
                    </button>
                    <button
                      onClick={() => handleDelete(content.id)}
                      className="inline-flex items-center px-2 py-1 border border-red-300 shadow-sm text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <Trash2 className="mr-1 h-3 w-3" />
                      삭제
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* 모달 */}
      {showModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowModal(false)}></div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      {selectedContent ? '콘텐츠 수정' : '콘텐츠 생성'} - {contentTypeNames[contentType]}
                    </h3>
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">제목</label>
                      <input
                        type="text"
                        name="title"
                        defaultValue={selectedContent?.title || ''}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    {contentType === 'footer' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">회사명</label>
                          <input
                            type="text"
                            name="company_name"
                            defaultValue={selectedContent?.company_name || ''}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">주소</label>
                          <textarea
                            name="company_address"
                            rows={2}
                            defaultValue={selectedContent?.company_address || ''}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">전화번호</label>
                            <input
                              type="text"
                              name="company_phone"
                              defaultValue={selectedContent?.company_phone || ''}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">이메일</label>
                            <input
                              type="email"
                              name="company_email"
                              defaultValue={selectedContent?.company_email || ''}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">사업자등록번호</label>
                          <input
                            type="text"
                            name="business_number"
                            defaultValue={selectedContent?.business_number || ''}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">저작권 표시</label>
                          <input
                            type="text"
                            name="copyright_text"
                            defaultValue={selectedContent?.copyright_text || ''}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                      </>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700">내용</label>
                      <textarea
                        name="content"
                        rows={10}
                        defaultValue={selectedContent?.content || ''}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        placeholder="HTML 형식으로 입력 가능합니다."
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={saveContentMutation.isLoading}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    <Save className="mr-2 h-4 w-4" />
                    {saveContentMutation.isLoading ? '저장 중...' : '저장'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    취소
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteContentManagement;

